import {Box, CircularProgress, useTheme} from '@mui/material';
import {isValidElement} from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MermaidBlock from './MermaidBlock';

type AiMarkdownViewerProps = {
  content: string;
  loading: boolean;
};

const AiMarkdownViewer = ({content, loading}: AiMarkdownViewerProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        flex: 1,
        px: 3,
        py: 1,
        overflow: 'auto',
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.body2.fontSize,
        lineHeight: 1.5,
        '& p': {my: 0.5},
        '& ul, & ol': {my: 0.5, pl: 3},
        '& li': {my: 0.25},
        '& h1, & h2, & h3, & h4': {mt: 1.5, mb: 0.5, lineHeight: 1.3},
        '& pre': {
          my: 0.5,
          p: 1,
          borderRadius: 1,
          bgcolor: 'background.paper',
          overflow: 'auto'
        },
        '& code': {fontSize: theme.typography.caption.fontSize},
        '& hr': {my: 1, borderColor: theme.palette.divider},
        '& table': {borderCollapse: 'collapse', my: 0.5, width: '100%'},
        '& th, & td': {
          border: 1,
          borderColor: 'divider',
          px: 1.5,
          py: 0.5,
          textAlign: 'left'
        },
        '& th': {bgcolor: 'action.selected', fontWeight: 'bold'}
      }}
    >
      {loading ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={{
            code({className, children}) {
              if (className === 'language-mermaid') {
                return <MermaidBlock chart={String(children)} />;
              }
              return <code className={className}>{children}</code>;
            },
            pre({children}) {
              if (isValidElement(children) && children.type === MermaidBlock) {
                return <>{children}</>;
              }
              return <pre>{children}</pre>;
            }
          }}
        >
          {content}
        </Markdown>
      )}
    </Box>
  );
};

export default AiMarkdownViewer;
